import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Yii } from 'libraries/interfaces/Yii';
import { IAirSearchQuantity } from './air-search-form/air-search-quantity-form/air-search-quantity-form.component';
import {
    EWayType,
    IAirSearchSegment,
    IAirport,
    IBegin
} from "./air-search-form/air-search-segment-oneway-return/air-search-segment-oneway-return.component";
import {
    AirSearchHeaderActionService
} from './air-search-header/air-search-header-action/air-search-header-action.service';
import { Subscription } from 'rxjs';
import {
    IAirSearchFlexibility
} from "app_locobook/air-search/air-search-form/air-search-flexibility-form/air-search-flexibility-form.component";
import { airSearchFormDefaultValues } from './air-search-form/air-search-form-default-values';
import { PermissionResourceService } from 'libraries/service/resources/permission/permissionResource.service';
import { EAirSearchPermissions } from './e-air-search-permissions';
import {
    IAirSearchPrice
} from "app_locobook/air-search/air-search-form/air-search-price-form/air-search-price-form.component";
import {
    IAirSearchCarrier
} from "app_locobook/air-search/air-search-form/air-search-carrier-form/air-search-carrier-form.component";
import { AirSearchResourceService } from "libraries/service/resources/air-search-resource/air-search-resource.service";
import { IBookingExternalLink } from 'libraries/interfaces/i-post-air-search-request';
import {
    IOnBookingCreatedNotificationSetup,
    IOnOfferSelectedNotificationSetup,
    IOnBookingCreationFailedNotificationSetup
} from 'libraries/service/resources/model/booking/air/IAirCreateBooking';
import { EAirSearchFields } from './e-air-search-fields';
import {
    EExternalApplicationName,
    ExternalApplicationService
} from 'app_locobook/service/external-application/external-application.service';
import * as moment from 'moment';
import _cloneDeep from "lodash-es/cloneDeep";
import {
    IEnd
} from "app_locobook/air-search/air-search-form/air-search-segment-multiple/air-search-segment-multiple.component";

class Wording {
    public readonly loader: string = Yii.t('wui-iqba-js', 'Chargement du formulaire');
}

export interface IAirSearchRequest {
    initialisationRequestContent?: IAirSearchRequest;
    quantity?: IAirSearchQuantity;
    segment?: IAirSearchSegment[];
    price?: IAirSearchPrice;
    fareType?: string[];
    carrier?: IAirSearchCarrier;
    keyword?: string[];
    flexibility?: IAirSearchFlexibility;
    cabin?: string;
    maxVias?: number;
    host?: string[] | string;
    locator?: string[];
    fullServiceCarriersOnly?: boolean;
    wayType?: EWayType;
    bookingExternalLink?: IBookingExternalLink;
    onBookingCreatedNotificationArray?: IOnBookingCreatedNotificationSetup[];
    onOfferSelectedNotificationArray?: IOnOfferSelectedNotificationSetup[];
    onBookingCreationFailedNotificationArray?: IOnBookingCreationFailedNotificationSetup[];
    externalContextId?: string;
    languageCode?: string;
    traveller?: IAirSearchTraveller[];
    booking?: IAirSearchBooking;
}

interface IAirSearchBooking {
    code: string;
    syscode: string;
    owner: string;
    host: string;
}

interface IAirSearchTraveller {
    birthDate: string;
    firstName: string;
    lastName: string;
    title: string;
    type: string;
}

export enum EAirSearchContext {
    pilotage = 'pilotage',
    watabi = 'watabi',
    updateBooking = 'updateBooking'
}

interface IRequest {
    body: IAirSearchRequest;
}

@Component({
    selector: 'air-search',
    templateUrl: './air-search.component.html',
    styleUrls: ['./air-search.component.scss']
})
export class AirSearchComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    public searchErrorMessage: string;
    @Input()
    public canUseAirSearchForm: boolean = false;
    @Output()
    public readonly onSubmitSearch: EventEmitter<IAirSearchRequest> = new EventEmitter<IAirSearchRequest>();
    public readonly wording: Wording = new Wording();
    public isLoad: boolean = false;
    public isAirSearchFormVisible: boolean;
    public hasAirSearchRequest: boolean;
    public airSearchHeaderActionSubscription: Subscription;
    public getSearchRequestSubscription: Subscription;
    public airSearchRequest: IAirSearchRequest = null;
    public airSearchFormValues: IAirSearchRequest = null;
    public airSearchContext: EAirSearchContext = null;
    public wayType: EWayType;

    private onSearchIdTripChangeSubscription: Subscription = new Subscription();

    constructor(
        private readonly route: ActivatedRoute,
        private readonly airSearchHeaderActionService: AirSearchHeaderActionService,
        private readonly permissionResourceService: PermissionResourceService,
        private readonly externalApplicationService: ExternalApplicationService,
        private readonly airSearchResourceService: AirSearchResourceService,
    ) {
    }

    public ngOnInit(): void {
        this.initAirSearchPermission();
        this.onSearchIdTripChangeSubscription = this.airSearchResourceService.onSearchIdTripChange
            .subscribe({
                next: (idTrip: string): void => {
                    if (idTrip) {
                        this.initAirSearchRequestWithTripId(idTrip);
                    } else {
                        this.init();
                    }
                    this.initCanUseForm();
                }
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.searchErrorMessage?.currentValue && !changes?.searchErrorMessage.firstChange) {
            this.isLoad = false;
        }
    }

    public ngOnDestroy(): void {
        if (this.getSearchRequestSubscription) {
            this.getSearchRequestSubscription.unsubscribe();
        }
        if (this.airSearchHeaderActionSubscription) {
            this.airSearchHeaderActionSubscription.unsubscribe();
        }
        if (this.onSearchIdTripChangeSubscription) {
            this.onSearchIdTripChangeSubscription.unsubscribe();
        }
    }

    public onSearch(formValues: IAirSearchRequest): void {
        this.canUseAirSearchForm = true;
        this.airSearchFormValues = formValues;
        this.airSearchRequest = this.formatAirSearchRequest();
        this.initIsAirSearchFormVisible(true);
        this.initIsAirSearchRequest();
        this.onSubmitSearch.emit(this.airSearchRequest);
    }

    private initAirSearchPermission(): void {
        this.isLoad = true;
        this.permissionResourceService.hasPermission(EAirSearchPermissions.IQBA_SEARCHFORM_QUANTITY).subscribe({
            next: (): void => {
                // force get permission
                this.initAirSearchRequest();
            }
        });
    }

    private initAirSearchRequest(): void {
        const parameters: Params = this.route.snapshot.params;
        const post: string = sessionStorage.getItem('$post');

        // #35164
        // when we grab some services, the url seams to be rebuild.
        // so I found a way to take idTrip with another way.
        const parts: string[] = window.location.pathname.split('/search/');
        const idFromUrl: string = parts.length > 1 ? parts[1].split('/').length ? parts[1].split('/')[0] : null : null;
        const idTrip: string = parameters?.idSearch || idFromUrl;

        if (idTrip) {
            this.initAirSearchRequestWithTripId(idTrip);
        } else if (post) {
            sessionStorage.removeItem('$post');
            const airSearchRequest: IAirSearchRequest = JSON.parse(post);
            this.airSearchRequest = {
                ...airSearchRequest,
                host: this.getHostArray(airSearchRequest)
            };
            this.initIsAirSearchRequest();

            const externalApplicationName: string = this.externalApplicationService.getExternalApplicationName();
            if (externalApplicationName === EExternalApplicationName.watabi) {
                this.airSearchContext = EAirSearchContext.watabi;
            } else {
                this.airSearchContext = this.isUpdateBookingRequest(this.airSearchRequest)
                    ? EAirSearchContext.updateBooking
                    : EAirSearchContext.pilotage;
            }
            if (this.airSearchContext === EAirSearchContext.updateBooking) {
                this.init();
            }
            /*
            we dont initialize form at this time.
            we wait that the RS is recieved in search.comopnent, then
            airSearchResourceService.onSearchIdTripChange is next
            and init is launch at this time.
             */
        } else {
            this.init();
        }
    }

    private isUpdateBookingRequest(request: IAirSearchRequest): boolean {
        return Boolean(request.booking?.host)
            && Boolean(request.booking?.code)
            && Boolean(request.booking?.owner)
            && Boolean(request.booking?.syscode);
    }

    private initAirSearchRequestWithTripId(idTrip: string): void {
        this.isLoad = true;
        this.getSearchRequestSubscription = this.airSearchResourceService.getSearchRequest(idTrip)
            .subscribe(
                (request: IRequest): void => {
                    this.airSearchRequest = request?.body ?? null;
                    // this.airSearchContext = this.airSearchRequest?.segment?.length > 2 ? EAirSearchContext.pilotage : null;
                    this.init();
                    this.initCanUseForm();
                },
                (error: Error): void => {
                    this.isLoad = false;
                    // TODO handle error
                }
            );
    }

    private init(): void {
        this.initWayType();
        this.initAirSearchFormValues();
        this.initIsAirSearchFormVisible();
        this.initIsAirSearchRequest();
        this.initAirSearchHeaderActionSubscription();
        this.isLoad = false;
    }

    private initCanUseForm(): void {
        // @ts-ignore
        const searchFormPermissionsInRequest: object = this.airSearchRequest?.permission?.searchForm || this.airSearchRequest?.initialisationRequestContent?.permission?.searchForm;
        if (searchFormPermissionsInRequest) {
            // @ts-ignore
            if (Object.keys(this.airSearchRequest?.permission?.searchForm).includes('use')) {
                // @ts-ignore
                this.canUseAirSearchForm = this.airSearchRequest.permission.searchForm.use;
            } else {
                this.canUseAirSearchForm = true;
            }
        } else {
            this.canUseAirSearchForm = true;
        }
    }

    private initWayType(): void {
        this.wayType = this.airSearchContext === EAirSearchContext.pilotage ? EWayType.Multiple : this.airSearchRequest?.wayType ?? EWayType.Return;
        this.airSearchHeaderActionService.onWayTypeChange(this.wayType);
    }

    private initIsAirSearchFormVisible(isSearchLaunch: boolean = false): void {
        if (isSearchLaunch) {
            this.isAirSearchFormVisible = false;
        } else {
            this.isAirSearchFormVisible = this.airSearchContext === EAirSearchContext.watabi ||
                this.airSearchContext === EAirSearchContext.updateBooking ||
                !Boolean(this.airSearchRequest);
        }
    }

    private initIsAirSearchRequest(): void {
        this.hasAirSearchRequest = Boolean(this.airSearchRequest);
    }

    private initAirSearchFormValues(): void {
        this.airSearchFormValues = this.formatAirSearchFormValues();
    }

    private initAirSearchHeaderActionSubscription(): void {
        this.airSearchHeaderActionSubscription = new Subscription();
        this.airSearchHeaderActionSubscription.add(
            this.airSearchHeaderActionService.resetAirSearchFormObservable.subscribe(() => {
                this.airSearchRequest = null;
            }));
        this.airSearchHeaderActionSubscription.add(
            this.airSearchHeaderActionService.wayTypeChangeObservable.subscribe((wayType: EWayType): void => {
                this.wayType = wayType;
            }));
        this.airSearchHeaderActionSubscription.add(
            this.airSearchHeaderActionService.isAirSearchFormVisibleObservable.subscribe((isAirSearchFormVisible: boolean): void => {
                this.isAirSearchFormVisible = isAirSearchFormVisible;
            }));
    }

    private formatAirSearchRequest(): IAirSearchRequest {
        return {
            ...this.airSearchRequest,
            ...this.airSearchFormValues,
            segment: this.getSegmentRequest(),
            carrier: this.getCarrierRequest(),
            price: this.getPriceRequest(),
            host: this.getHostRequest(),
            flexibility: this.getFlexibilityRequest(),
            wayType: this.wayType
        };
    }

    private getSegmentRequest(): IAirSearchSegment[] {
        return this.airSearchFormValues.segment.map((segment: IAirSearchSegment): IAirSearchSegment => {
            const baseSegment = this.getSegmentFromToAirportRequest(segment);
            return {
                ...baseSegment,
                ...(this.wayType === EWayType.Multiple && {
                    cabin: this.airSearchFormValues.cabin ?? null,
                    maxVias: this.airSearchFormValues.maxVias ?? null,
                }),
            };
        });
    }

    private getSegmentFromToAirportRequest(segment: IAirSearchSegment): IAirSearchSegment {
        return {
            ...segment,
            from: segment.isFromAirport ? ({airport: {iata: segment.from}} as IAirport) : segment.from,
            to: segment.isToAirport ? ({airport: {iata: segment.to}} as IAirport) : segment.to,
        };
    }

    private getCarrierRequest(): IAirSearchCarrier {
        return this.airSearchFormValues.carrier &&
        (this.airSearchFormValues.carrier.exclude?.length ||
            this.airSearchFormValues.carrier.include?.length ||
            this.airSearchFormValues.carrier.preferred?.length) ?
            this.airSearchFormValues.carrier : null;
    }

    private getPriceRequest(): IAirSearchPrice {
        return {
            ...(this.airSearchFormValues?.price?.fareType?.length > 0 && {fareType: this.airSearchFormValues?.price?.fareType}),
            ...(this.airSearchFormValues?.price?.value > 0 && {value: this.airSearchFormValues?.price?.value * 100}),
            ...(this.airSearchFormValues?.price?.plusOrMinus?.percentage > 0 && {
                plusOrMinus: {
                    percentage: this.airSearchFormValues?.price?.plusOrMinus?.percentage
                }
            })
        };
    }

    private getHostRequest(): string[] {
        return this.airSearchFormValues.fullServiceCarriersOnly && this.airSearchFormValues.host?.includes(EAirSearchFields.fullServiceCarriersOnly) ?
            (this.airSearchFormValues.host as string[])?.filter((host) => host !== EAirSearchFields.fullServiceCarriersOnly) :
            (this.airSearchFormValues.host as string[]);
    }

    private getFlexibilityRequest(): IAirSearchFlexibility {
        return this.airSearchFormValues?.segment?.length === 2 && this.airSearchFormValues?.flexibility ? this.airSearchFormValues?.flexibility : null;
    }

    private formatAirSearchFormValues(): IAirSearchRequest {
        return this.airSearchRequest ? {
            ...this.airSearchRequest,
            cabin: this.getCabinFormValue(),
            maxVias: this.getMaxViasFormValue(),
            segment: this.getSegmentFormValue(),
            quantity: this.getQuantityFormValue(),
            price: this.getPriceFormValue(),
            flexibility: this.getFlexibilityFormValue(),
        } : airSearchFormDefaultValues;
    }

    private getCabinFormValue(): string {
        if (this.wayType !== EWayType.Multiple) {
            return this.airSearchRequest.segment?.length && this.airSearchRequest.segment[0].cabin ?
                this.airSearchRequest.segment[0].cabin :
                airSearchFormDefaultValues.cabin;
        }
        return null;
    }

    private getMaxViasFormValue(): number {
        if (this.wayType !== EWayType.Multiple) {
            return this.airSearchRequest.segment?.length && this.airSearchRequest.segment[0].maxVias >= 0 ?
                this.airSearchRequest.segment[0].maxVias :
                airSearchFormDefaultValues.maxVias;
        }
        return null;
    }

    private getSegmentFormValue(): IAirSearchSegment[] {
        return this.airSearchRequest.segment?.map((segment: IAirSearchSegment): IAirSearchSegment => {
            let begin: Date | IBegin = null;
            let duration = null;
            const beginDate: Date | IBegin = this.getSegmentBeginDate(segment, this.airSearchContext);
            if (this.airSearchContext === EAirSearchContext.pilotage || this.airSearchRequest.segment.length > 2) {
                begin = {
                    ...segment.begin,
                    ...beginDate,
                };
            } else {
                begin = beginDate;
            }
            // @ts-ignore
            const end: IEnd = segment?.end ?
                {
                    ...segment.end,
                    between: segment?.end?.between?.max ? {
                        max: segment.end.between?.max?.time ?
                            {time: this.removeSeconds(segment.end.between.max.time)} : null,
                    } : null,
                } : null;

            duration = segment.viaArray?.length && segment.viaArray[0].duration ?
                {
                    ...segment.viaArray[0].duration,
                    unit: segment.viaArray[0].duration.xUnit ?? null
                } : null;
            return {
                ...this.getSegmentFromToAirportFormValues(segment),
                begin,
                // @ts-ignore
                end,
                duration
            };
        });
    }

    private getSegmentFromToAirportFormValues(segment: IAirSearchSegment): IAirSearchSegment {
        const isFromAirport: boolean = segment.isFromAirport || Boolean((segment.from as IAirport).airport?.iata);
        const isToAirport: boolean = segment.isToAirport || Boolean((segment.to as IAirport).airport?.iata);
        return {
            ...segment,
            isFromAirport,
            from: isFromAirport ? (segment.from as IAirport).airport.iata : segment.from,
            isToAirport,
            to: isToAirport ? (segment.to as IAirport).airport.iata : segment.to,
        };
    }

    private getPriceFormValue(): IAirSearchPrice {
        return {
            ...this.airSearchRequest?.price,
            value: this.airSearchRequest?.price?.value > 0 ? this.airSearchRequest?.price?.value / 100 : 0
        };
    }

    private getFlexibilityFormValue(): IAirSearchFlexibility {
        return this.airSearchRequest?.segment?.length === 2 && this.airSearchRequest?.flexibility ? this.airSearchRequest?.flexibility : null;
    }

    private getHostArray(airSearchRequest: IAirSearchRequest): string[] {
        if (airSearchRequest?.host) {
            return Array.isArray(airSearchRequest?.host) ? airSearchRequest.host : [airSearchRequest.host];
        }
        return [];
    }

    private getQuantityFormValue(): IAirSearchQuantity {
        if (this.airSearchContext === EAirSearchContext.updateBooking || this.airSearchContext === EAirSearchContext.watabi) {
            return this.getQuantityFromTravellers();
        }
        return this.airSearchRequest?.quantity ? this.airSearchRequest?.quantity : {
            adult: 1,
            child: 0,
            infant: 0
        };
    }

    private getQuantityFromTravellers(): IAirSearchQuantity {
        const quantity = {
            adult: 0,
            child: 0,
            infant: 0
        };
        this.airSearchRequest.traveller?.forEach((traveller: IAirSearchTraveller) => {
            if (traveller.type && traveller.type.toLowerCase() in EAirSearchFields) {
                quantity[traveller.type.toLowerCase()]++;
            }
        });
        return quantity;
    }

    private getSegmentBeginDate(segment: IAirSearchSegment, context: EAirSearchContext): Date | IBegin {
        // @ts-ignore
        const hasBetween: boolean = segment?.begin?.between;
        if (!hasBetween) {
            return (context === EAirSearchContext.pilotage || this.airSearchRequest.segment.length > 2) ? {
                value: (segment.begin as IBegin)?.value ?
                    moment((segment.begin as IBegin).value).toDate() :
                    segment.begin ?
                        moment(segment.begin as Date).toDate() :
                        null,
            } : (segment.begin as IBegin)?.value ?
                moment((segment.begin as IBegin).value).toDate() :
                segment.begin ?
                    moment(segment.begin as Date).toDate() :
                    null;
        }
        if ((segment.begin as IBegin).between?.min?.time) {
            (segment.begin as IBegin).between.min.time = this.removeSeconds((segment.begin as IBegin).between.min.time);
        }
        if ((segment.begin as IBegin).between?.max?.time) {
            (segment.begin as IBegin).between.max.time = this.removeSeconds((segment.begin as IBegin).between.max.time);
        }
        return {
            ...segment.begin,
            value: moment((segment.begin as IBegin).value).toDate(),
        };

    }

    private removeSeconds(time: string): string {
        if (time?.length > 5) {
            return time
                .split(':')
                .slice(0, -1)
                .join(':');
        }
        return time;
    }
}
